import { Injectable } from "@angular/core";
import { environment } from "../environments/environment";
import { HttpClient } from "@angular/common/http";
import { of } from "rxjs";

const onboardingEmailStorageKeyPrefix = "onboarding-email:";

export interface TenantInfo {
  emailAddress: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  tenantName?: string;
  username?: string;
}

export interface AtlasCheckoutRequest {
  subscriptionLevel: "base" | "pro" | "enterprise";
  priceId?: string;
  tenantName?: string;
  tenantInfo?: TenantInfo;
}

export interface CheckoutPlan {
  subscriptionLevel: AtlasCheckoutRequest["subscriptionLevel"];
  priceId?: string | null;
  displayName: string;
  displayDetail?: string | null;
  currency?: string | null;
  unitAmount?: number | null;
  interval?: string | null;
}

export interface RegistrationPayload {
  tenantInfo: TenantInfo;
  solutionInfo?: unknown;
  registrationInfo: {
    provider: "datastorio_saas" | "aws_marketplace";
    provider_interface_id: string;
  };
}

export interface RegistrationResponse {
  result: string;
  status?: string;
  tenant_name?: string;
}

export interface PendingOnboardingVerificationResponse {
  result: string;
  state: string;
  status: string;
  email: string;
  tenant_name: string;
  verified_at?: string;
}

export interface PendingOnboardingActivationResponse {
  result: string;
  state: string;
  status: string;
  tenant_name: string;
  email: string;
}

export interface PendingOnboardingStatusResponse {
  result: string;
  state: string;
  status: string;
  tenant_name: string;
  checkout_done: boolean;
  provisioning_started: boolean;
  atlas_ready: boolean;
  provisioning_failed: boolean;
  atlas_solution_status?: string | null;
  atlas_portal_url?: string | null;
}

export function loadOnboardingEmail(tenantId?: string | null): string | null {
  const normalizedTenantId = tenantId?.trim();
  if (!normalizedTenantId || typeof sessionStorage === "undefined") {
    return null;
  }

  const emailAddress = sessionStorage.getItem(`${onboardingEmailStorageKeyPrefix}${normalizedTenantId}`);
  const normalizedEmail = emailAddress?.trim();
  return normalizedEmail || null;
}

export function saveOnboardingEmail(tenantId?: string | null, emailAddress?: string | null): void {
  const normalizedTenantId = tenantId?.trim();
  if (!normalizedTenantId || typeof sessionStorage === "undefined") {
    return;
  }

  const storageKey = `${onboardingEmailStorageKeyPrefix}${normalizedTenantId}`;
  const normalizedEmail = emailAddress?.trim();
  if (!normalizedEmail) {
    sessionStorage.removeItem(storageKey);
    return;
  }

  sessionStorage.setItem(storageKey, normalizedEmail);
}

@Injectable({
  providedIn: "root",
})
export class RegistrationService {
  constructor(private http: HttpClient) {}

  register(payload: RegistrationPayload) {
    if (environment.disableTenantApi) {
      return of({ result: "success", status: "email_sent", tenant_name: payload.tenantInfo.tenantName } as RegistrationResponse);
    }
    return this.http.post<RegistrationResponse>(`${environment.API_URL}/tenants`, payload);
  }

  createCheckoutSession(tenantId: string, payload: AtlasCheckoutRequest) {
    if (environment.disableTenantApi) {
      return of({ checkout_url: "/atlas/checkout/success" });
    }
    return this.http.post<{ checkout_url: string }>(
      `${environment.API_URL}/tenants/${encodeURIComponent(tenantId)}/checkout_sessions`,
      payload,
    );
  }

  getCheckoutPlans() {
    if (environment.disableTenantApi) {
      return of({
        plans: [
          {
            subscriptionLevel: "base",
            priceId: "price_base",
            displayName: "Base",
            displayDetail: "Launch up to 3 tenants.",
            currency: "USD",
            unitAmount: 0,
            interval: "month",
          },
          {
            subscriptionLevel: "pro",
            priceId: "price_pro",
            displayName: "Pro",
            displayDetail: "Scale with automation and alerts.",
            currency: "USD",
            unitAmount: 19900,
            interval: "month",
          },
          {
            subscriptionLevel: "enterprise",
            priceId: "price_enterprise",
            displayName: "Enterprise",
            displayDetail: "Dedicated support and security reviews.",
            currency: "USD",
            unitAmount: null,
            interval: "month",
          },
        ] as CheckoutPlan[],
      });
    }

    return this.http.get<{ plans: CheckoutPlan[] }>(`${environment.API_URL}/stripe/plans`);
  }

  validateToken(token: string) {
    if (environment.disableTenantApi) {
      return of({ allowed: true });
    }
    return this.http.get(
      `${environment.API_URL}/tenants/mkpl_can_register/${encodeURIComponent(token)}`
    );
  }

  verifyOnboarding(tenantId: string, token: string) {
    if (environment.disableTenantApi) {
      return of({
        result: "success",
        state: "verified",
        status: "identity_created",
        email: "user@example.com",
        tenant_name: tenantId,
      } as PendingOnboardingVerificationResponse);
    }
    return this.http.get<PendingOnboardingVerificationResponse>(
      `${environment.API_URL}/tenants/onboarding/${encodeURIComponent(tenantId)}/verify?token=${encodeURIComponent(token)}`,
    );
  }

  resendVerification(tenantId: string) {
    if (environment.disableTenantApi) {
      return of({ result: "success", state: "resent", status: "email_sent", tenant_name: tenantId });
    }
    return this.http.post(
      `${environment.API_URL}/tenants/onboarding/${encodeURIComponent(tenantId)}/resend_verification`,
      {},
    );
  }

  activateOnboarding(tenantId: string, token: string, password: string) {
    if (environment.disableTenantApi) {
      return of({
        result: "success",
        state: "activated",
        status: "checkout_pending",
        tenant_name: tenantId,
        email: "user@example.com",
      } as PendingOnboardingActivationResponse);
    }
    return this.http.post<PendingOnboardingActivationResponse>(
      `${environment.API_URL}/tenants/onboarding/${encodeURIComponent(tenantId)}/activate`,
      { token, password },
    );
  }

  getOnboardingStatus(tenantId: string) {
    if (environment.disableTenantApi) {
      return of({
        result: "success",
        state: "provisioning",
        status: "checkout_started",
        tenant_name: tenantId,
        checkout_done: true,
        provisioning_started: true,
        atlas_ready: false,
        provisioning_failed: false,
        atlas_solution_status: "enabling",
        atlas_portal_url: null,
      } as PendingOnboardingStatusResponse);
    }
    return this.http.get<PendingOnboardingStatusResponse>(
      `${environment.API_URL}/tenants/onboarding/${encodeURIComponent(tenantId)}/status`,
    );
  }
}
